/**
 * useTableSearch — shared template cho ô Search trong Filter Area.
 *
 * Default behaviour: search trên TẤT CẢ cột (case-insensitive substring).
 * Đây là contract chung của Filter Area "Search" — mọi list page phải dùng
 * hook này thay vì copy-paste `rowMatchesSearch` riêng.
 *
 * Nếu page cần giới hạn cột search (hiếm), truyền `searchFields` để override
 * — nhưng phải có lý do nghiệp vụ rõ ràng, vì user expect Search là full-table.
 */
import { useMemo } from 'react'

/** Column shape chấp nhận được — chỉ cần field `key` để đọc giá trị. */
export interface SearchableColumn<R> {
  key: keyof R | string
}

/**
 * Kiểm tra một row có match với search term không.
 * Trả về `true` khi `term` rỗng để caller không cần guard.
 */
export function rowMatchesSearch<R extends Record<string, any>>(
  row: R,
  term: string,
  fields: ReadonlyArray<SearchableColumn<R> | keyof R | string>,
): boolean {
  const needle = term.trim().toLowerCase()
  if (!needle) return true
  return fields.some((f) => {
    const key = (typeof f === 'object' ? f.key : f) as keyof R
    const value = row[key]
    if (value == null) return false
    return String(value).toLowerCase().includes(needle)
  })
}

/**
 * Hook filter rows theo search term.
 *
 * @param rows    Mảng đầy đủ (baseRows) — KHÔNG phải rows đã paginate.
 * @param fields  Danh sách cột/keys cần search. Truyền `TABLE_COLUMNS` để
 *                search toàn bộ cột (default expectation của Filter Area Search).
 * @param term    Search term raw từ TextField.
 */
export function useTableSearch<R extends Record<string, any>>(
  rows: readonly R[],
  fields: ReadonlyArray<SearchableColumn<R> | keyof R | string>,
  term: string,
): R[] {
  return useMemo(
    () => rows.filter((row) => rowMatchesSearch(row, term, fields)),
    [rows, fields, term],
  )
}
